import React from 'react';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { showErrorNotification, showSuccessNotification } from 'src/components/widgets/notificationToaster';
import { dataURLtoFile, T, uploadComposerPreviewImage } from 'src/shared/helpers';
import MaterialIcon from 'src/shared/material_icons/image/texture.svg';
import ScreenshotIcon from 'src/shared/material_icons/image/photo_camera.svg';
import styled from 'styled-components';
import { BasicButton } from '../../../../../../widgets/buttons';
import { ComposerSlot } from '../../../configurator-components/composer-editors/composerSlots';
import { readMaterialPreview } from '../../../configurator-components/scene-editors/materialPreview';

interface Composer3DPreviewCaptureProps {
    materials: BABYLON.Material[];
    onImagePick: (imageUrl: string, tempImageFileKey?: string) => void | Promise<void | string>;
    successMessage: string;
    helperText?: string;
    disabled?: boolean;
}

const CapturePanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    padding: 10px;
    border: 1px solid var(--editor-line, #d1d3d8);
    border-radius: 8px;
    background: var(--editor-sunken, #e9ebed);
`;

const CaptureDescription = styled.p`
    margin: 0;
    color: var(--editor-faint, #676a6e);
    font-size: 11px;
    line-height: 16px;
`;

const CaptureRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;

    /* IL MATERIALE DA FOTOGRAFARE È UNO SLOT (utente, 2026-10-15), come ogni altra scelta dell'Inspector:
       la sfera, il nome, e la riga apre l'elenco con la ricerca. Era una tendina nativa del browser. */
    > div:last-child {
        min-width: 0;
        flex: 1;
    }
`;

const CaptureStatus = styled.span`
    color: var(--editor-muted, #55595f);
    font-size: 11px;
`;

const Composer3DPreviewCapture: React.FC<Composer3DPreviewCaptureProps> = ({ materials, onImagePick, successMessage, helperText, disabled = false }) => {
    const [sceneEditor] = usePreview3DStore((store) => [store.sceneEditor]);
    const [selectedMaterialID, setSelectedMaterialID] = React.useState('');
    const [capturing, setCapturing] = React.useState<'object' | 'material' | null>(null);

    React.useEffect(() => {
        if (selectedMaterialID && materials.some((material) => material.id === selectedMaterialID)) return;
        setSelectedMaterialID(materials[0]?.id ?? '');
    }, [materials, selectedMaterialID]);

    const capture = async (type: 'object' | 'material') => {
        if (!sceneEditor || capturing) return;
        if (type === 'material' && !selectedMaterialID) {
            showErrorNotification({ message: T._('Select a material before taking its screenshot.', 'Admin') });
            return;
        }

        setCapturing(type);
        try {
            const imageData =
                type === 'material'
                    ? await sceneEditor.getMaterialScreenshotAsync(selectedMaterialID, 512, 512)
                    : await sceneEditor.getSceneContentScreenshotAsync(512, 512);
            if (!imageData) throw new Error('The 3D editor did not return an image.');

            const fileName = type === 'material' ? `${selectedMaterialID}-preview.png` : 'object-preview.png';
            const image = await uploadComposerPreviewImage(dataURLtoFile(imageData, fileName));

            await onImagePick(image.url, image.key);
            showSuccessNotification({ message: successMessage });
        } catch (error) {
            console.error('Composer 3D preview capture failed:', error);
            showErrorNotification({ message: T._('The 3D preview image could not be generated. Try again.', 'Admin') });
        } finally {
            setCapturing(null);
        }
    };

    const controlsDisabled = disabled || !sceneEditor || capturing !== null;

    return (
        <CapturePanel>
            <CaptureDescription>
                {helperText ??
                    T._(
                        'Create the preview image from the 3D scene instead of uploading one. Use object captures the object as it is shown now, Use material captures the selected material.',
                        'Admin'
                    )}
            </CaptureDescription>
            <CaptureRow>
                <BasicButton type='button' disabled={controlsDisabled} onClick={() => void capture('object')}>
                    <ScreenshotIcon />
                    {T._('Use object', 'Admin')}
                </BasicButton>
            </CaptureRow>
            <CaptureRow>
                <BasicButton type='button' disabled={controlsDisabled || !selectedMaterialID} onClick={() => void capture('material')}>
                    <MaterialIcon />
                    {T._('Use material', 'Admin')}
                </BasicButton>
                <div>
                    <ComposerSlot
                        id='composer-preview-capture-material'
                        value={selectedMaterialID || undefined}
                        options={materials.map((material) => {
                            const url = readMaterialPreview(sceneEditor, material.id);
                            return {
                                value: material.id,
                                label: material.name || material.id,
                                preview: url ? <img src={url} alt='' /> : undefined
                            };
                        })}
                        glyph={<MaterialIcon />}
                        caption={T._('Material', 'Admin')}
                        emptyLabel={T._('Choose a material', 'Admin')}
                        searchPlaceholder={T._('Search materials...', 'Admin')}
                        noOptionsLabel={T._('No materials available', 'Admin')}
                        disabled={controlsDisabled}
                        onChange={setSelectedMaterialID}
                    />
                </div>
            </CaptureRow>
            {capturing && <CaptureStatus>{T._('Generating and uploading the preview…', 'Admin')}</CaptureStatus>}
            {!sceneEditor && <CaptureStatus>{T._('The 3D scene must be loaded before capturing an image.', 'Admin')}</CaptureStatus>}
        </CapturePanel>
    );
};

export default Composer3DPreviewCapture;
